import { createClient, sendVisitBeacon, type PushbackClient } from "@pushback-and-chaos/client";
import type { VisitBeacon } from "@pushback-and-chaos/contracts";
import { PreviewClient } from "./preview-client.js";
import { noteRefusal } from "./region-refusal.js";

/**
 * The ONLY place in apps/web that knows a URL exists.
 *
 * Everything else in the app talks to a PushbackClient instance. This is
 * enforced two ways rather than by convention: eslint's no-restricted-globals
 * bans `fetch`/`XMLHttpRequest`/`EventSource`/`WebSocket` everywhere under
 * src/ except this file, and dependency-cruiser forbids apps/web from
 * importing packages/db, packages/engine, packages/llm or apps/api at all.
 *
 * In dev the base URL is `/api`, which vite.config.ts proxies to apps/api on
 * the same origin — so no CORS, and SSE arrives unbuffered.
 */
const BASE_URL: string = typeof import.meta.env["VITE_API_BASE_URL"] === "string" ? import.meta.env["VITE_API_BASE_URL"] : "/api";

let singleton: PushbackClient | null = null;

export function apiClient(): PushbackClient {
  // `__PC_PREVIEW__` is a compile-time constant, so a normal build folds this
  // branch away and never bundles the preview fixtures at all.
  // Every refusal the client reads is told to the region gate's watch (data/region-refusal.ts),
  // so its one screen appears whichever door was refused.
  singleton ??= __PC_PREVIEW__ ? new PreviewClient({ baseUrl: BASE_URL, onProblem: noteRefusal }) : createClient({ baseUrl: BASE_URL, onProblem: noteRefusal });
  return singleton;
}

/**
 * The visit beacon (ADR 0022): `POST /a/v`, fire and forget. `sendBeacon`
 * first, so a view counted as the page closes still goes; a plain request
 * where the browser has none. The answer is always an empty 204 and is never
 * read. Only data/visit-count.ts calls this, and only after GPC, DNT and the
 * opt-out have said yes.
 */
export function countVisit(beacon: VisitBeacon): void {
  const nav = globalThis.navigator as Navigator | undefined;
  sendVisitBeacon(BASE_URL, beacon, {
    ...(typeof nav?.sendBeacon === "function" ? { sendBeacon: (url: string, data: Blob) => nav.sendBeacon(url, data) } : {}),
    fetch: (url, init) => globalThis.fetch(url, init),
  });
}

/** True when this build has no backend to talk to (a static preview deploy). */
export const IS_PREVIEW: boolean = __PC_PREVIEW__;

/** True when the dev panel should be compiled in at all. Tree-shaken to `false` in production. */
export const DEV_PANEL_ENABLED: boolean = __PC_DEV_PANEL__;
